/**
 * RACE CONDITION FIX:
 * This component previously had a race condition where it would render before the target
 * DOM elements (main content and navigation) were available, causing skip links to not appear.
 *
 * SOLUTION - Callback Ref Pattern:
 * 1. SkipLinks creates callback refs for target elements
 * 2. Parent component receives these callback refs via props
 * 3. Parent passes the callback refs to target elements
 * 4. When target elements mount, they call the callback refs
 * 5. SkipLinks updates its state and re-renders with visible buttons
 *
 * This ensures skip links are always rendered when target elements are available.
 */

import { useState, useCallback, useEffect } from 'react';

import { getKeyboardFocusableElements } from '@legacy/utils/accessibility';
import { Button, Paper, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import styles from './SkipLinks.module.scss';

interface SkipLinksProps {
  // Callback to receive the main content callback ref
  onMainContentRefReady?: (ref: (element: HTMLElement | null) => void) => void;
  // Callback to receive the navigation callback ref
  onNavigationRefReady?: (ref: (element: HTMLElement | null) => void) => void;
}

export const SkipLinks = ({
  onMainContentRefReady,
  onNavigationRefReady,
}: SkipLinksProps) => {
  const { t } = useTranslation('header');

  // Local state to track when target elements become available
  // These will trigger re-renders when elements mount/unmount
  const [mainContentElement, setMainContentElement] =
    useState<HTMLElement | null>(null);
  const [navigationElement, setNavigationElement] =
    useState<HTMLElement | null>(null);

  // Callback refs that will be passed to the parent component
  // These refs will be called when target elements mount/unmount
  const mainContentCallbackRef = useCallback((element: HTMLElement | null) => {
    setMainContentElement(element);
  }, []);

  const navigationCallbackRef = useCallback((element: HTMLElement | null) => {
    setNavigationElement(element);
  }, []);

  // Notify parent of our callback refs when this component mounts
  // The parent will then pass these callback refs to the target elements
  useEffect(() => {
    onMainContentRefReady?.(mainContentCallbackRef);
    onNavigationRefReady?.(navigationCallbackRef);
  }, [
    onMainContentRefReady,
    onNavigationRefReady,
    mainContentCallbackRef,
    navigationCallbackRef,
  ]);

  const skipToMainContent = () => {
    if (mainContentElement) {
      mainContentElement.setAttribute('tabIndex', '-1');
      mainContentElement.focus();
    }
  };

  const skipToNavigation = () => {
    if (navigationElement) {
      navigationElement.setAttribute('tabIndex', '-1');
      // get first focusable element of top navigation and focus it
      const [firstElement] = getKeyboardFocusableElements(navigationElement);
      if (firstElement) {
        firstElement.focus();
      } else {
        navigationElement.focus();
      }
    }
  };

  return (
    <Paper className={styles.SkipLinks}>
      <Stack gap={1}>
        {mainContentElement && (
          <Button
            onClick={skipToMainContent}
            data-skip-link
            data-automation-id="SkipToContentButton"
            variant="contained"
          >
            {t('SkipToContentButton')}
          </Button>
        )}

        {navigationElement && (
          <Button
            onClick={skipToNavigation}
            data-skip-link
            data-automation-id="SkipToTopNavigationButton"
            variant="contained"
          >
            {t('SkipToNavigationButton')}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};
